import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import debounce from "lodash.debounce";
import Header from "../../components/common/Header";
import AddCustomerForm from "../../components/AddCustomer";
import CustomerCard from "../../components/CustomerCard";
import SearchBar from "../../components/common/SearchBar";
import { getSalonUsers } from "../../redux/actions/SalonDetails.action";
import Cross from "../../assets/search-icon.gif";
import Button from "../../components/common/Button";
import Footer from "../../components/common/Footer";
import { parseJwt } from "../../utils";
import { SALON_DETAILS_ROUTE } from "../../constants";
import "./GenerateBill.scss";

function GenerateBill({ getSalonUsers, salonUsers }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 1. Debounced API call setup
  const debouncedGetUsers = useMemo(
    () =>
      debounce((term) => {
        if (term.length > 0) {
          getSalonUsers({
            salonId: parseJwt().salon_id,
            searchTerm: term,
          });
          // We set loading to false shortly after triggering.
          // If your Redux has a 'loading' state, use that instead for perfect sync.
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      }, 600), // 600ms delay for a smooth typing experience
    [getSalonUsers],
  );

  // Clean up debounce on component unmount
  useEffect(() => {
    return () => {
      debouncedGetUsers.cancel();
    };
  }, [debouncedGetUsers]);

  const onSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim().length > 0) {
      setIsLoading(true);
      debouncedGetUsers(value);
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("away");
    }
  }, []);

  const onCancelAddCustomer = () => {
    setShowAddCustomerModal(false);
    setSearchTerm("");
  };

  const gotoBillGeneration = (user) => {
    // Ensure we use 'mobile' and 'username' keys from your dynamic data
    sessionStorage.setItem("on_behalf_of_mobile_no", user.mobile?.trim());
    sessionStorage.setItem("on_behalf_of_username", user.username?.trim());
    window.location.href = `${SALON_DETAILS_ROUTE}/${parseJwt().salon_id}`;
  };

  return (
    <div className="generate-bill-page">
      <div className="search-bar-container">
        <Header label={"Bill Generation"} className="generate-bill-header" />
        {!showAddCustomerModal && (
          <SearchBar onSearch={onSearch} searchTerm={searchTerm} />
        )}
      </div>

      <div className="search-results-container">
        {showAddCustomerModal ? (
          <div className="add-customer-container">
            <AddCustomerForm
              onCancel={onCancelAddCustomer}
              onAdd={gotoBillGeneration}
            />
          </div>
        ) : (
          <>
            {searchTerm.length === 0 ? (
              <div className="empty-search-state">
                <div className="search-initial">
                  <div className="icon-container">
                    <img
                      src={Cross}
                      alt="Searching icon animation"
                      className="search-gif-icon"
                    />
                  </div>
                  <h2 className="empty-search-title">Find Your Customer</h2>
                  <p className="empty-search-description">
                    Use the search bar above to find existing customers by their
                    name or phone number
                  </p>
                </div>
              </div>
            ) : (
              <div className="search-results">
                {/* Check loading state first to prevent 'No results' flicker */}
                {isLoading ? (
                  <div className="searching-loader">
                    <p>Searching for customers...</p>
                  </div>
                ) : salonUsers && salonUsers.length > 0 ? (
                  salonUsers.map((user, index) => (
                    <CustomerCard
                      key={user.id || index}
                      user={user}
                      index={index}
                      onCardClick={() => {
                        gotoBillGeneration(user);
                      }}
                    />
                  ))
                ) : (
                  <div className="no-results">
                    <h2 className="no-results-title">No Customers Found</h2>
                    <p className="no-results-description">
                      We couldn't find any customers matching "{searchTerm}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {!showAddCustomerModal && searchTerm.length > 0 && !isLoading ? (
        <Footer className={"generate-bill-footer"}>
          <Button
            text="+ Add New"
            type="next"
            className={"create-new-button"}
            onClick={() => setShowAddCustomerModal(true)}
          />
        </Footer>
      ) : null}
    </div>
  );
}

const mapStateToProps = ({ SalonDetailsReducer }) => ({
  salonUsers: SalonDetailsReducer.salonUsers,
});

const mapDispatchToProps = {
  getSalonUsers: getSalonUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(GenerateBill);
